import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { SimilarityBar } from '../../components/common/SimilarityBar';
import { CropImage } from '../../components/common/CropImage';
import { DecisionBadge, DecisionBadgeConfig } from '../../components/common/DecisionBadge';
import { VariantPickerPanel } from '../../components/common/VariantPickerPanel';
import {
  ForgeCandidateVariant,
  ForgeFDClassificationPayload,
} from '../../types';
import { filterCandidates, MAX_NUMBER_KEYS } from '../../utils/filterCandidates';

type FDDecisionType = 'confirm' | 'false_alarm' | 'reassign' | 'bad_image';

interface FDDecision {
  type: FDDecisionType;
  variantId?: string;
}

interface FDClassificationUIProps {
  payload: ForgeFDClassificationPayload;
  onSelect: (data: { decisions: Record<string, FDDecision> }) => void;
}

export const FDClassificationUI: React.FC<FDClassificationUIProps> = ({
  payload,
  onSelect,
}) => {
  const { detections, candidateVariants } = payload;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [decisions, setDecisions] = useState<Record<string, FDDecision>>({});
  const [showVariantPicker, setShowVariantPicker] = useState(false);
  const [selectedVariantIdx, setSelectedVariantIdx] = useState(0);
  const currentDetection = detections[currentIndex];
  const currentDecision = currentDetection ? decisions[currentDetection.id] : undefined;
  const completedCount = Object.keys(decisions).length;

  useEffect(() => {
    onSelect({ decisions });
  }, [decisions, onSelect]);

  const filteredCandidates = useMemo(
    () => filterCandidates(candidateVariants, currentDetection?.upc),
    [currentDetection, candidateVariants]
  );

  const setDecision = useCallback(
    (detectionId: string, decision: FDDecision) => {
      setDecisions((prev) => ({ ...prev, [detectionId]: decision }));
    },
    []
  );

  const advanceToNext = useCallback(
    (skipId: string) => {
      const nextIdx = detections.findIndex((d, i) => i > currentIndex && !decisions[d.id]);
      if (nextIdx !== -1) {
        setCurrentIndex(nextIdx);
      } else {
        const wrapIdx = detections.findIndex((d) => !decisions[d.id] && d.id !== skipId);
        if (wrapIdx !== -1) setCurrentIndex(wrapIdx);
      }
    },
    [currentIndex, detections, decisions]
  );

  const handleConfirm = useCallback(() => {
    if (!currentDetection) return;
    setDecision(currentDetection.id, { type: 'confirm' });
    setShowVariantPicker(false);
    advanceToNext(currentDetection.id);
  }, [currentDetection, setDecision, advanceToNext]);

  const handleFalseAlarm = useCallback(() => {
    if (!currentDetection) return;
    setDecision(currentDetection.id, { type: 'false_alarm' });
    setShowVariantPicker(false);
    advanceToNext(currentDetection.id);
  }, [currentDetection, setDecision, advanceToNext]);

  const handleReassign = useCallback(() => {
    if (!currentDetection) return;
    setShowVariantPicker(true);
    setSelectedVariantIdx(0);
  }, [currentDetection]);

  const handleSelectVariant = useCallback(
    (variant: ForgeCandidateVariant) => {
      if (!currentDetection) return;
      setDecision(currentDetection.id, { type: 'reassign', variantId: variant.id });
      setShowVariantPicker(false);
      advanceToNext(currentDetection.id);
    },
    [currentDetection, setDecision, advanceToNext]
  );

  const handleBadImage = useCallback(() => {
    if (!currentDetection) return;
    setDecision(currentDetection.id, { type: 'bad_image' });
    setShowVariantPicker(false);
    advanceToNext(currentDetection.id);
  }, [currentDetection, setDecision, advanceToNext]);

  const navigateUp = useCallback(() => {
    if (showVariantPicker) {
      setSelectedVariantIdx((prev) => Math.max(0, prev - 1));
    } else {
      setCurrentIndex((prev) => Math.max(0, prev - 1));
      setShowVariantPicker(false);
    }
  }, [showVariantPicker]);

  const navigateDown = useCallback(() => {
    if (showVariantPicker) {
      setSelectedVariantIdx((prev) => Math.min(filteredCandidates.length - 1, prev + 1));
    } else {
      setCurrentIndex((prev) => Math.min(detections.length - 1, prev + 1));
      setShowVariantPicker(false);
    }
  }, [showVariantPicker, filteredCandidates.length, detections.length]);

  const handleEscape = useCallback(() => {
    if (showVariantPicker) setShowVariantPicker(false);
  }, [showVariantPicker]);

  const numberHandlers = useMemo(() => {
    const handlers: Record<string, () => void> = {};
    for (let i = 1; i <= MAX_NUMBER_KEYS; i++) {
      const idx = i - 1;
      handlers[String(i)] = () => {
        if (showVariantPicker && filteredCandidates[idx]) {
          handleSelectVariant(filteredCandidates[idx]);
        }
      };
    }
    return handlers;
  }, [showVariantPicker, filteredCandidates, handleSelectVariant]);

  useKeyboardShortcuts({
    c: handleConfirm,
    w: handleFalseAlarm,
    r: handleReassign,
    b: handleBadImage,
    arrowup: navigateUp,
    arrowdown: navigateDown,
    escape: handleEscape,
    ...numberHandlers,
  });

  if (!currentDetection) return null;

  return (
    <div style={{ display: 'flex', height: '100%', gap: 0 }}>
      {/* Left: Detection list */}
      <div
        style={{
          width: 260,
          minWidth: 260,
          borderRight: '1px solid var(--border)',
          overflowY: 'auto',
          background: 'var(--surface)',
        }}
      >
        <div
          style={{
            padding: '12px 14px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>
            FD Detections
          </span>
          <span
            style={{
              fontSize: 11,
              fontFamily: 'var(--font-mono)',
              color: 'var(--text-tertiary)',
            }}
          >
            {completedCount}/{detections.length}
          </span>
        </div>
        <div style={{ padding: '2px 0' }}>
          {detections.map((det, idx) => {
            const dec = decisions[det.id];
            const isActive = idx === currentIndex;
            return (
              <div
                key={det.id}
                onClick={() => {
                  setCurrentIndex(idx);
                  setShowVariantPicker(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 14px',
                  cursor: 'pointer',
                  background: isActive ? 'var(--elevated)' : 'transparent',
                  borderLeft: isActive ? '2px solid var(--accent)' : '2px solid transparent',
                  transition: 'background 80ms ease',
                }}
              >
                <CropImage
                  imageUrl={det.cropImageUrl}
                  variantColor={det.variantColor}
                  size={36}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {det.productName}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      fontFamily: 'var(--font-mono)',
                      color: fdScoreColor(det.fdScore),
                    }}
                  >
                    FD {(det.fdScore * 100).toFixed(0)}%
                  </div>
                </div>
                {dec && <DecisionBadge config={FD_DECISION_CONFIGS[dec.type]} />}
              </div>
            );
          })}
        </div>
      </div>

      {/* Center: Detection detail */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Info bar */}
        <div
          className="task-info-banner"
          style={{ margin: 0, padding: '8px 20px', borderRadius: 0 }}
        >
          <span className="info-banner-id">{currentDetection.id}</span>
          <span className="info-banner-upc">{currentDetection.upc}</span>
          <div className="info-banner-sim">
            <SimilarityBar value={currentDetection.fdScore} />
          </div>
          <span className="info-banner-hint">FD Score</span>
        </div>

        <div
          style={{
            flex: 1,
            display: 'flex',
            gap: 20,
            padding: 20,
            overflow: 'hidden',
          }}
        >
          {/* Crop image & pipeline log */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', gap: 16 }}>
              {/* Crop image */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  background: 'var(--elevated)',
                  borderRadius: 'var(--radius)',
                  border: '1px solid var(--border)',
                  padding: 12,
                  minHeight: 160,
                }}
              >
                <CropImage
                  imageUrl={currentDetection.cropImageUrl}
                  variantColor={currentDetection.variantColor}
                  size={160}
                  borderRadius={6}
                />
              </div>
              {/* Assigned variant (if any) */}
              {currentDetection.assignedVariantImageUrl && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Assigned to
                  </div>
                  <div
                    style={{
                      background: 'var(--elevated)',
                      borderRadius: 'var(--radius)',
                      border: '1px solid var(--border)',
                      padding: 8,
                    }}
                  >
                    <img
                      src={currentDetection.assignedVariantImageUrl}
                      alt="assigned variant"
                      style={{ width: 100, height: 100, borderRadius: 4, objectFit: 'cover' }}
                    />
                  </div>
                  <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-tertiary)' }}>
                    {currentDetection.assignedVariantId}
                  </span>
                </div>
              )}
            </div>

            {/* Pipeline log */}
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  marginBottom: 8,
                }}
              >
                Pipeline Log
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {currentDetection.detectionSteps.map((step, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '7px 10px',
                      background: i % 2 === 0 ? 'var(--elevated)' : 'transparent',
                      borderRadius: 'var(--radius-sm)',
                    }}
                  >
                    <StepStatusDot status={step.status} />
                    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', minWidth: 120 }}>
                      {step.step}
                    </span>
                    {i < currentDetection.detectionSteps.length - 1 && (
                      <ArrowForwardIcon style={{ fontSize: 12, color: 'var(--text-tertiary)', opacity: 0.5 }} />
                    )}
                    <span style={{ flex: 1, fontSize: 12, color: 'var(--text-primary)' }}>
                      {step.result}
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        fontFamily: 'var(--font-mono)',
                        fontWeight: 600,
                        color: stepConfColor(step.confidence),
                      }}
                    >
                      {(step.confidence * 100).toFixed(0)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Decision buttons */}
            <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
              <button
                onClick={handleConfirm}
                className="btn-secondary"
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  padding: '10px 12px',
                  fontSize: 13,
                  fontWeight: 600,
                  ...(currentDecision?.type === 'confirm'
                    ? { background: 'var(--positive)', color: '#fff', borderColor: 'var(--positive)' }
                    : {}),
                }}
              >
                <CheckCircleOutlineIcon style={{ width: 16, height: 16 }} />
                Confirm
                <span className="kbd" style={{ marginLeft: 4 }}>C</span>
              </button>
              <button
                onClick={handleFalseAlarm}
                className="btn-secondary"
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  padding: '10px 12px',
                  fontSize: 13,
                  fontWeight: 600,
                  ...(currentDecision?.type === 'false_alarm'
                    ? { background: 'var(--warning)', color: '#fff', borderColor: 'var(--warning)' }
                    : {}),
                }}
              >
                <ReportProblemOutlinedIcon style={{ width: 16, height: 16 }} />
                Wrong
                <span className="kbd" style={{ marginLeft: 4 }}>W</span>
              </button>
              <button
                onClick={handleReassign}
                className="btn-secondary"
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  padding: '10px 12px',
                  fontSize: 13,
                  fontWeight: 600,
                  ...(currentDecision?.type === 'reassign'
                    ? { background: 'var(--accent)', color: '#fff', borderColor: 'var(--accent)' }
                    : {}),
                }}
              >
                <SwapHorizIcon style={{ width: 16, height: 16 }} />
                Reassign
                <span className="kbd" style={{ marginLeft: 4 }}>R</span>
              </button>
              <button
                onClick={handleBadImage}
                className="btn-secondary"
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  padding: '10px 12px',
                  fontSize: 13,
                  fontWeight: 600,
                  ...(currentDecision?.type === 'bad_image'
                    ? { background: 'var(--danger)', color: '#fff', borderColor: 'var(--danger)' }
                    : {}),
                }}
              >
                <BrokenImageOutlinedIcon style={{ width: 16, height: 16 }} />
                Bad
                <span className="kbd" style={{ marginLeft: 4 }}>B</span>
              </button>
            </div>
          </div>

          {/* Right: Variant picker (shown on Reassign) */}
          {showVariantPicker && (
            <VariantPickerPanel
              title="Reassign to Variant"
              candidates={filteredCandidates}
              selectedIdx={selectedVariantIdx}
              onSelectIdx={setSelectedVariantIdx}
              onPick={handleSelectVariant}
              chosenVariantId={
                currentDecision?.type === 'reassign' ? currentDecision.variantId : undefined
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ── Helpers ──

const FD_DECISION_CONFIGS: Record<FDDecisionType, DecisionBadgeConfig> = {
  confirm: { label: 'OK', bg: 'rgba(16,185,129,0.15)', color: 'var(--positive)' },
  false_alarm: { label: 'FA', bg: 'rgba(217,119,6,0.15)', color: 'var(--warning)' },
  reassign: { label: 'RE', bg: 'rgba(59,130,246,0.15)', color: 'var(--accent)' },
  bad_image: { label: 'B', bg: 'rgba(239,68,68,0.15)', color: 'var(--danger)' },
};

function fdScoreColor(score: number): string {
  if (score >= 0.90) return 'var(--danger)';
  if (score >= 0.75) return 'var(--warning)';
  return 'var(--text-secondary)';
}

function stepConfColor(confidence: number): string {
  if (confidence >= 0.90) return 'var(--positive)';
  if (confidence >= 0.75) return 'var(--warning)';
  return 'var(--danger)';
}

const StepStatusDot: React.FC<{ status: 'pass' | 'fail' | 'warning' }> = ({ status }) => {
  const colors = { pass: 'var(--positive)', fail: 'var(--danger)', warning: 'var(--warning)' };
  return (
    <div
      style={{
        width: 7,
        height: 7,
        borderRadius: '50%',
        background: colors[status],
        flexShrink: 0,
      }}
    />
  );
};
